// ============================================================
// CLUBHUB – Mitgliederliste: die Ansicht in der Adresse
// ============================================================
// Wie im Agent (`kunden/ansicht.ts`): Filter, Suche und Sortierung
// stehen in den searchParams. So laesst sich jede Ansicht als
// Lesezeichen ablegen und weitergeben, und der Rueckweg aus einem
// Mitglied fuehrt in die Liste MIT ihrem Filter zurueck.
// ============================================================
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";

/** Die Zustaende oben im Filterkopf — „Mitglieder“ ist die Vorgabe. */
export const ZUSTAENDE = [
  { schluessel: "", label: "Mitglieder", hinweis: "Aktive, passive und Ehrenmitglieder" },
  { schluessel: "aktiv", label: "Aktiv", hinweis: "Aktive Mitglieder" },
  { schluessel: "passiv", label: "Passiv", hinweis: "Passive und fördernde Mitglieder" },
  { schluessel: "ehren", label: "Ehrenmitglieder", hinweis: "Ehrenmitglieder" },
  { schluessel: "interessent", label: "Interessenten", hinweis: "Aufnahmeantrag gestellt, noch nicht aufgenommen" },
  { schluessel: "ehemalig", label: "Ehemalige", hinweis: "Ausgetreten oder verstorben" },
  { schluessel: "kontakt", label: "Kontakte", hinweis: "Kein Mitglied: Sponsoren, Lieferanten, Fans" },
  { schluessel: "alle", label: "Alle", hinweis: "Alle Personen und Organisationen" },
] as const;
export type Zustand = (typeof ZUSTAENDE)[number]["schluessel"];

/** Pflegefaelle — ein Filter, kein Schild in jeder Zeile (wie im Agent). */
export const PRUEFUNGEN = [
  { schluessel: "mandat", label: "Lastschrift ohne Mandat", hinweis: "Zahlt per Lastschrift, aber kein gültiges SEPA-Mandat" },
  { schluessel: "beitrag", label: "Ohne Beitrag", hinweis: "Aktives oder passives Mitglied ohne Beitragsart" },
  { schluessel: "mail", label: "Ohne Mailadresse", hinweis: "Bekommt Post statt Mail oder gar nichts" },
  { schluessel: "offen", label: "Offene Beträge", hinweis: "Mindestens eine offene oder überfällige Rechnung" },
] as const;
export type Pruefung = (typeof PRUEFUNGEN)[number]["schluessel"] | "";

export const SORT_COLUMNS = {
  name: { label: "Name", first: "auf" },
  nummer: { label: "Nr.", first: "auf" },
  ort: { label: "Ort", first: "auf" },
  eintritt: { label: "Eintritt", first: "ab" },
  alter: { label: "Alter", first: "auf" },
  offen: { label: "Offen", first: "ab" },
} as const satisfies SortColumns<string>;
export type SortKey = keyof typeof SORT_COLUMNS;
export const DEFAULT_SORT: SortKey = "name";

export type Ansicht = {
  q: string;
  zustand: Zustand;
  gruppe: string;
  pruefung: Pruefung;
  sort: SortState<SortKey>;
};

/** Diese Parameter setzt „Filter zurücksetzen“ zurück. */
export const ALLE_PARAMS = ["q", "zustand", "gruppe", "pruefung", "sortierung", "richtung", "umfang"] as const;

const IST_ZUSTAND = new Set<string>(ZUSTAENDE.map((z) => z.schluessel));
const IST_PRUEFUNG = new Set<string>(PRUEFUNGEN.map((p) => p.schluessel));

export function ansichtLesen(p: Record<string, string | string[] | undefined>): Ansicht {
  const eins = (name: string) => {
    const w = p[name];
    return (Array.isArray(w) ? w[0] : w) ?? "";
  };
  const zustand = eins("zustand");
  const pruefung = eins("pruefung");
  return {
    q: eins("q").trim(),
    zustand: (IST_ZUSTAND.has(zustand) ? zustand : "") as Zustand,
    gruppe: eins("gruppe"),
    pruefung: (IST_PRUEFUNG.has(pruefung) ? pruefung : "") as Pruefung,
    sort: readSort(p, SORT_COLUMNS, DEFAULT_SORT),
  };
}

/** Wie viele Filter gerade wirken (fuer „Filter zurücksetzen“). */
export function aktiveFilter(a: Ansicht): number {
  return [a.q, a.zustand, a.gruppe, a.pruefung].filter(Boolean).length;
}
